import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquarePlus } from "lucide-react";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";

export const RFI_STATE_LABELS = { open: "Open — with architect", answered: "Answered — with PM", closed: "Closed", void: "Void" };

function NewRfiForm({ pid, onCreated }) {
  const [f, setF] = useState({ subject: "", question: "", spec_section: "", due_date: "" });
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    const body = { subject: f.subject, question: f.question, spec_section: f.spec_section };
    if (f.due_date) body.due_date = f.due_date;
    api.post(`/phase3/rfis/projects/${pid}`, body)
      .then(() => { setF({ subject: "", question: "", spec_section: "", due_date: "" }); setError(null); onCreated(); })
      .catch((err) => setError(errMsg(err, "Could not raise the RFI.")));
  };
  return (
    <form onSubmit={submit} className="grid gap-3 md:grid-cols-[2fr_1fr_1fr]" data-testid="phase3-rfi-form">
      <label className="text-sm font-nav text-erf-dark">Subject
        <Input required value={f.subject} onChange={set("subject")} className="mt-1 h-11" data-testid="phase3-rfi-subject" />
      </label>
      <label className="text-sm font-nav text-erf-dark">Spec section
        <Input value={f.spec_section} onChange={set("spec_section")} placeholder="08 71 00" className="mt-1 h-11" data-testid="phase3-rfi-spec" />
      </label>
      <label className="text-sm font-nav text-erf-dark">Answer needed by
        <Input type="date" value={f.due_date} onChange={set("due_date")} className="mt-1 h-11" data-testid="phase3-rfi-due" />
      </label>
      <label className="text-sm font-nav text-erf-dark md:col-span-3">Question (at least 10 characters)
        <Textarea required value={f.question} onChange={set("question")} className="mt-1" data-testid="phase3-rfi-question" />
      </label>
      <div className="md:col-span-3">
        <Button type="submit" className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="phase3-rfi-submit">
          <MessageSquarePlus className="w-4 h-4 mr-1.5" aria-hidden="true" />Raise RFI
        </Button>
      </div>
      {error && <p role="alert" className="text-sm text-[#991B1B] md:col-span-3" data-testid="phase3-rfi-error">{error}</p>}
    </form>
  );
}

export function RfiPanel({ pid, manager }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`/phase3/rfis/projects/${pid}`).then((r) => setRows(r.data)), [pid]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="ds-card border-erf-borderCream mt-6" data-testid="phase3-rfi-panel">
      <CardContent className="p-5">
        <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg mb-3">Requests for Information (RFIs)</h2>
        {manager && <div className="mb-4"><NewRfiForm pid={pid} onCreated={load} /></div>}
        <ul className="space-y-2">
          {rows.length === 0 && <li className="text-sm font-nav text-erf-dark/80">No RFIs on this project yet.</li>}
          {rows.map((r) => (
            <li key={r.id}>
              <Link to={`/phase3/rfis/${r.id}`} data-testid={`phase3-rfi-row-${r.number}`}
                className="flex flex-wrap items-center gap-2 min-h-[44px] rounded-lg border border-erf-borderCream bg-white px-3 py-2 text-sm font-nav text-erf-dark hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-gold">
                <strong>{r.number}</strong><span className="mr-auto">{r.subject}</span>
                <span className="text-erf-dark/80">{RFI_STATE_LABELS[r.state]}{r.due_date ? ` · due ${r.due_date}` : ""}</span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
